import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * "Install App" prompt for browsers that support beforeinstallprompt
 * (Chrome/Edge desktop & Android). iOS Safari does not fire this event;
 * iOS users add the app via Share → Add to Home Screen (the meta tags in
 * index.html make that flow nice).
 *
 * Auto-hides if:
 *  - the app is already installed (display-mode: standalone)
 *  - the user dismissed it (stored in localStorage for 30 days)
 */
export default function InstallPwaButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed → hide
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone) return; // iOS

    // Recently dismissed → hide for 30 days
    const dismissedAt = Number(localStorage.getItem("pink_pwa_dismissed_at") || 0);
    if (dismissedAt && Date.now() - dismissedAt < 30 * 24 * 60 * 60 * 1000) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => setVisible(false);
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setVisible(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem("pink_pwa_dismissed_at", String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] max-w-xs"
      data-testid="install-pwa-prompt"
    >
      <div className="bg-[#0a0a0a] border border-purple-500/30 rounded-xl p-4 shadow-[0_0_30px_rgba(192,38,211,0.25)]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-purple-500/10 rounded-lg shrink-0">
            <Download className="w-4 h-4 text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">Install Purple INK</p>
            <p className="text-xs text-neutral-400 mt-0.5">
              Add to your home screen for quick access.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={install}
                className="text-xs font-bold uppercase tracking-wider text-white bg-purple-600 hover:bg-purple-500 border border-purple-400 rounded-md px-3 py-1.5 transition-colors"
                data-testid="install-pwa-confirm"
              >
                Install
              </button>
              <button
                onClick={dismiss}
                className="text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
                data-testid="install-pwa-dismiss"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-neutral-600 hover:text-neutral-400"
            aria-label="Dismiss"
            data-testid="install-pwa-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
